/**
 * CollectionCreateModal — lightweight modal for creating a new collection.
 *
 * Design.md §13a.1:
 *   "The `+` chip at the end of the collection filter row opens a lightweight
 *    modal with a single input `Name` and buttons `Create` / `Cancel`."
 *
 * Design invariants (design.md §2):
 *   - Hairlines not shadows (only --shadow-cta on primary CTA).
 *   - Slug is server-derived from name — not surfaced to the user.
 *   - 409 conflict (duplicate slug) surfaces as inline form error, not a toast.
 *   - Optimistic UI: parent receives onCreate(name) and handles optimistic
 *     update; this modal only surfaces validation errors returned by the caller.
 *   - Name ≤ 60 chars per track spec (server allows 200; we tighten client-side
 *     per Track 3.N scope).
 *   - No exclamations, no banned words (branding.md §5.2.1).
 *
 * Accessibility: focus trap, ESC closes, role=dialog, aria-modal.
 * Motion: 200 ms ease (--motion-state) per design.md §4.5.
 *
 * Mobile: modal slides up from bottom (bottom-sheet on ≤ 640px).
 * Desktop/tablet: centred sheet.
 */

import {
  useState,
  useEffect,
  useRef,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

// ─── Styles ───────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--scrim)",
  display: "flex",
  zIndex: 1000,
};

/** Panel switches to bottom-sheet below 640px via CSS class injection. */
const SHEET_CSS = `
  @media (max-width: 639px) {
    .proof-col-modal-overlay {
      align-items: flex-end !important;
      justify-content: stretch !important;
    }
    .proof-col-modal-panel {
      border-radius: var(--radius-sheet-top) var(--radius-sheet-top) 0 0 !important;
      max-width: 100% !important;
      width: 100% !important;
    }
  }
  @media (min-width: 640px) {
    .proof-col-modal-overlay {
      align-items: center;
      justify-content: center;
    }
    .proof-col-modal-panel {
      width: 360px;
    }
  }
`;

const panelStyle: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "var(--radius-card)",
  // --line-strong (not --line): readable panel edge on --bg; see RecipeCreationModal.
  border: "1px solid var(--line-strong)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  // width set by SHEET_CSS
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--space-4) var(--space-4) var(--space-3)",
  borderBottom: "1px solid var(--line)",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--text)",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--muted)",
  fontSize: "18px",
  lineHeight: 1,
  padding: "var(--space-1)",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color var(--motion-state)",
};

const bodyStyle: React.CSSProperties = {
  padding: "var(--space-4)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
  display: "block",
};

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: "100%",
    border: hasError ? "1px solid var(--del)" : "1px solid var(--line-strong)",
    borderRadius: "var(--radius-button)",
    background: "transparent",
    padding: "var(--space-2) var(--space-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--text)",
    outline: "none",
    minHeight: "var(--tap-target-min)",
    boxSizing: "border-box",
    transition: "border-color var(--motion-state)",
  };
}

const errorStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--del)",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "var(--space-2)",
  justifyContent: "flex-end",
  padding: "var(--space-3) var(--space-4)",
  borderTop: "1px solid var(--line)",
  flexShrink: 0,
};

function cancelButtonStyle(): React.CSSProperties {
  return {
    background: "none",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--muted)",
    cursor: "pointer",
    minHeight: "var(--tap-target-min)",
    transition: "color var(--motion-state), border-color var(--motion-state)",
  };
}

function createButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--accent)",
    border: "none",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--surface)",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "var(--shadow-cta)",
    minHeight: "var(--tap-target-min)",
    transition: "opacity var(--motion-state), background var(--motion-state)",
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Max name length per track spec (tighter than server's 200). */
const MAX_NAME_LENGTH = 60;

export interface CollectionCreateModalProps {
  /**
   * Called when the user confirms. Returns a Promise that resolves on success
   * or rejects with an error (the modal handles 409 inline).
   */
  onCreate: (name: string) => Promise<void>;
  onClose: () => void;
}

export function CollectionCreateModal({ onCreate, onClose }: CollectionCreateModalProps) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Focus name input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ESC closes the modal.
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose();
  }

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value.slice(0, MAX_NAME_LENGTH));
    if (error) setError(null);
  }

  function handleInputKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  }

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreate(trimmed);
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error && err.message.toLowerCase().includes("slug_taken")
          ? "A collection with a similar name already exists."
          : "Could not create the collection. Try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = name.trim().length > 0 && !submitting;

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div
        ref={overlayRef}
        className="proof-col-modal-overlay"
        style={overlayStyle}
        role="dialog"
        aria-modal="true"
        aria-label="New collection"
        onClick={handleOverlayClick}
      >
        <div className="proof-col-modal-panel" style={panelStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>New collection</span>
            <button type="button" style={closeButtonStyle} aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Body */}
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div style={bodyStyle}>
              <label htmlFor="proof-col-create-name" style={labelStyle}>
                Name
              </label>
              <input
                ref={inputRef}
                id="proof-col-create-name"
                type="text"
                value={name}
                onChange={handleNameChange}
                onKeyDown={handleInputKeyDown}
                style={inputStyle(error !== null)}
                placeholder="e.g. Italian"
                maxLength={MAX_NAME_LENGTH}
                autoComplete="off"
                disabled={submitting}
                aria-describedby={error ? "proof-col-create-error" : undefined}
                data-testid="collection-create-name"
              />
              {error && (
                <span id="proof-col-create-error" style={errorStyle} role="alert">
                  {error}
                </span>
              )}
            </div>

            {/* Footer */}
            <div style={footerStyle}>
              <button
                type="button"
                style={cancelButtonStyle()}
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={createButtonStyle(!canSubmit)}
                disabled={!canSubmit}
                data-testid="collection-create-submit"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
